/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo } from 'react'

import { t } from '@/i18n'
import { BrandNode, CarModelNode, CarSubModelNode } from '@/apollo/graphql'
import { OptionSelector } from '@/components'
import { Maybe } from '@/types'
import { useCarModelSelect } from './useCarModelSelect'

interface Props {
  isEditing?: boolean
  initialBrand: Maybe<Partial<BrandNode>>
  initialModel: Maybe<Partial<CarModelNode>>
  initialSubModel: Maybe<Partial<CarSubModelNode>>
  loading: boolean
  onTriggerChange: (f: string, v: any) => void
  [x: string]: any
}

const CarSubModelSelector: React.FC<Props> = ({
  isEditing,
  initialBrand,
  initialModel,
  initialSubModel,
  loading,
  onTriggerChange,
  ...rest
}) => {
  const {
    brandId,
    modelId,
    subModelId,
    brandOptions,
    modelOptions,
    subModelOptions,
    loadingBrands,
    loadingModels,
    loadingSubModels,
    brandsPageInfo,
    modelsPageInfo,
    subModelsPageInfo,
    onFetchMore,
    onFieldChange,
    onSearch,
  } = useCarModelSelect({
    loading,
    initialBrand,
    initialModel,
    initialSubModel,
  })

  useEffect(() => {
    onTriggerChange('carBrand', brandId || null)
  }, [onTriggerChange, brandId])

  useEffect(() => {
    onTriggerChange('carModel', modelId || null)
  }, [onTriggerChange, modelId])

  useEffect(() => {
    onTriggerChange('carSubModel', subModelId || null)
  }, [onTriggerChange, subModelId])

  const emptyValue = useMemo(() => (isEditing ? null : '...'), [isEditing])

  return (
    <>
      <OptionSelector
        {...rest}
        isSearchable
        isAsync
        required
        name="carBrand"
        menuPlacement="top"
        options={brandOptions}
        optionsLoading={loadingBrands}
        emptyValue={emptyValue}
        disabled={!Boolean(isEditing)}
        label={t('car_brand')}
        fieldValue={brandId}
        fetchMoreStatus={brandsPageInfo?.fetchMoreStatus}
        onFetchMore={() => onFetchMore('brands')}
        onFieldChange={(v: any) => onFieldChange('carBrand', v)}
        onSearch={(s: string) => onSearch('brands', s)}
      />
      <OptionSelector
        {...rest}
        isSearchable
        isAsync
        required
        name="carModel"
        menuPlacement="top"
        options={modelOptions}
        optionsLoading={loadingModels}
        emptyValue={emptyValue}
        disabled={!Boolean(isEditing)}
        label={t('car_model')}
        fieldValue={modelId}
        fetchMoreStatus={modelsPageInfo?.fetchMoreStatus}
        onFetchMore={() => onFetchMore('models')}
        onFieldChange={(v: any) => onFieldChange('carModel', v)}
        onSearch={(s: string) => onSearch('models', s)}
      />
      <OptionSelector
        {...rest}
        isSearchable
        isAsync
        required
        name="carSubModel"
        menuPlacement="top"
        options={subModelOptions}
        optionsLoading={loadingSubModels}
        emptyValue={emptyValue}
        disabled={!Boolean(isEditing)}
        label={t('car_sub_model')}
        fetchMoreStatus={subModelsPageInfo.fetchMoreStatus}
        onFetchMore={() => onFetchMore('submodels')}
        onFieldChange={(v: any) => onFieldChange('carSubModel', v)}
        onSearch={(s: string) => onSearch('submodels', s)}
      />
    </>
  )
}

export default CarSubModelSelector
